import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import NotifyPostStateHeader from './NotifyPostStateHeader';
import NotifyPostStateSummary from './NotifyPostStateSummary';
import NotifyPostStateCta from './NotifyPostStateCta';
import { CONSTANTS, NOOP } from './notifyUtils';
import NotifyBannerAnnouncement from './NotifyBannerAnnouncement';
import NotifyGdaPage from '../NotifyGdaPage/NotifyGdaPage';
import appContext from '../../constants/appContext';
import { Logger, openExternalUrl } from '../../utils';
import { getAnnouncements } from '../../../../consumer_web/app/modules/containers/AnnouncementContainer/AnnouncementAPI';

const PAGE_CONTEXT = 'set notify';
const EVENT_VIEW_GDA_MODULE = 'view gda module';
const EVENT_CLICK_GDA_LINK = 'click gda link';
const POPUP = 'pop-up';

const NotifyPostState = (props) => {
  const {
    Analytics,
    matchMedia,
    closeModalAndReload,
    context,
    isGda,
    locationCode,
    notifyStart,
    notifyEnd,
    notifyFlowState,
    selectedDate,
    selectedQuantity,
    showAvailability,
    venueName,
    eventName,
    deviceId,
  } = props;

  const [showNotifyGDAPage, setShowNotifyGDAPage] = useState(false);
  const [announcement, setAnnouncement] = useState(null);

  const { SET_NOTIFY_SUCCESS, POST_NOTIFY } = CONSTANTS;
  // Display GDA Banner if it is not widget, user non-GDA and the set notify is success
  const showGDABanner = context !== appContext.WIDGET && !isGda && notifyFlowState === SET_NOTIFY_SUCCESS;

  useEffect(() => {
    if (showGDABanner) {
      Analytics.event(EVENT_VIEW_GDA_MODULE, {
        context: PAGE_CONTEXT,
      });
    }
  }, []);

  useEffect(() => {
    if (showGDABanner && locationCode && deviceId) {
      const fetchAnnouncements = async () => {
        try {
          const response = await getAnnouncements({ deviceId, locationCode, flowContext: POST_NOTIFY });
          const normalizedResponse = response == null ? [] : response;
          const tiles = normalizedResponse.filter((item) => item.display?.web?.type === POPUP);
          setAnnouncement(tiles?.[0]);
        } catch (err) {
          Logger.error('[NotifyBannerAnnouncement] error fetching announcements', err);
        }
      };

      fetchAnnouncements();
    }
  }, [deviceId, locationCode, showGDABanner]);

  const handleBannerClick = () => {
    Analytics.event(EVENT_CLICK_GDA_LINK, {
      context: PAGE_CONTEXT,
    });
    if (announcement) {
      setShowNotifyGDAPage(true);
    } else {
      openExternalUrl('https://resy.com/global-dining-access#platinumoffer');
    }
  };

  const onClose = () => {
    setShowNotifyGDAPage(false);
    setAnnouncement(null);
    closeModalAndReload();
  };

  if (showNotifyGDAPage && announcement) {
    const { id, display = {}, action = {} } = announcement;
    const {
      title, image, content, action_text: ctaLabel,
    } = display;
    const { data } = action;
    const contentList = content?.split('\r\n');
    return (
      <NotifyGdaPage
        Analytics={Analytics}
        id={id}
        header={title}
        imageUrl={image}
        contentList={contentList}
        ctaLabel={ctaLabel}
        ctaLink={data?.url}
        isMobile={!matchMedia.md}
        onClose={onClose}
      />
    );
  }

  return (
    <div data-testid="NotifyPostState">
      <NotifyPostStateHeader context={context} isGda={isGda} notifyFlowState={notifyFlowState} />
      <NotifyPostStateSummary
        notifyStart={notifyStart}
        notifyEnd={notifyEnd}
        selectedDate={selectedDate}
        selectedQuantity={selectedQuantity}
        venueName={venueName}
        eventName={eventName}
      >
        {showGDABanner
          ? (
            <NotifyBannerAnnouncement
              onClick={handleBannerClick}
              announcement={announcement}
            />
          ) : null}
      </NotifyPostStateSummary>
      <NotifyPostStateCta
        closeModalAndReload={closeModalAndReload}
        isGda={isGda}
        notifyFlowState={notifyFlowState}
        showAvailability={showAvailability}
      />
    </div>
  );
};

NotifyPostState.propTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  closeModalAndReload: PropTypes.func,
  context: PropTypes.string,
  isGda: PropTypes.bool,
  locationCode: PropTypes.string.isRequired,
  notifyStart: PropTypes.string,
  notifyEnd: PropTypes.string,
  notifyFlowState: PropTypes.string.isRequired,
  selectedDate: PropTypes.instanceOf(Date),
  selectedQuantity: PropTypes.number,
  showAvailability: PropTypes.func,
  venueName: PropTypes.string,
  eventName: PropTypes.string,
  deviceId: PropTypes.string.isRequired,
};

NotifyPostState.defaultProps = {
  closeModalAndReload: NOOP,
  context: undefined,
  isGda: undefined,
  notifyStart: undefined,
  notifyEnd: undefined,
  selectedDate: undefined,
  selectedQuantity: undefined,
  showAvailability: NOOP,
  venueName: undefined,
  eventName: undefined,
};

export default NotifyPostState;
